import React, { ChangeEvent, useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import DashboardCreateSelectNetworkModal from "../modal/SelectNetworks";
import { Device, NetworkSelected } from "types";
import SelectedList, { TableColumn } from "./SelectedList";
import Icon from "@mui/material/Icon";
import { CHART_ERROR_MESSAGE, CHART_NAME_PATTERN, CreateChartState, useCreateDashboardStore } from "..";
import { ChartSource, ChartType } from "constant/chart";
import { EditWidgetProps } from ".";
import { BreakLine } from "components/common";
import Box from "@mui/material/Box";
import { getWidgetConfigByWidgetType, WidgetType } from "components/dashboard/utils/widgetConfig";

type CreateDataType = {
    chartName: string,
    attributes: NetworkSelected[],
}
const CreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Widget Name",
        },
        {
            key: "count",
            name: "No. of Rooms/Zones/Groups",
            render: (data: any) => <span>{data.attributes.length}</span>,
        },
    ],
    transform: (data: CreateDataType) => ({
        attributes: data.attributes.map((a) => (
            {
                device_id: a.ldsuUID,
                gateway_id: a.gatewayId,
                name: a.deviceName,
                said: 0
            }
        )),
        source: ChartSource.MULTIPLE_SENSORS,
        name: data.chartName,
        chart: ChartType.CARD_STATUS,
    }),
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.device_id}.chartName"]`,
            isValid: CHART_NAME_PATTERN.test((item.chartName ?? "").trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};
const Columns: TableColumn<NetworkSelected>[] = [
    { key: "deviceName", label: "Room/Zone/Group Name", className: "text-truncate col-3" },
    {
        key: "deviceType", label: "Type", className: "text-capitalize col-1",
    },
    { key: "gatewayName", label: "Network Name" },
    { key: "gatewayId", label: "Network ID" },
    {
        key: "actions",
        label: "",
        render: (
            item,
            props
        ) => (
            <Icon
                role="button"
                aria-label="remove-icon"
                className="cursor-pointer mr-2 text-primary-red-1"
                onClick={() => props.onRemove(item)}
            >
                close
            </Icon>
        ),
        className: "col-1 text-end",
    }
];

const CreateStatusWidget = ({
    panel,
    onChange,
}: EditWidgetProps) => {
    const { widget } = useCreateDashboardStore();
    const widgetType = widget?.widgetType ?? WidgetType.PSL_TABLE_STATUS;
    const widgetConfiguration = getWidgetConfigByWidgetType(widgetType);
    const defaultWidgetName =
        widgetConfiguration.defaultWidgetName || "Room/Zone/Group Status";
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [widgetName, setWidgetName] = useState(panel?.name ?? defaultWidgetName);
    const [selectedNetworks, setSelectedNetworks] = useState<Record<string, NetworkSelected>>({});
    const { deviceList } = useCreateDashboardStore();
    const { attributes } = panel ?? {};
    const networks = Object.values(selectedNetworks);
    const isEdit = !!panel?.uuid;

    useEffect(() => {
        useCreateDashboardStore.setState({
            createData: {
                ...CreateData,
                data: [{
                    chartName: widgetName,
                    attributes: Object.values(selectedNetworks),
                }],
                multiple: true,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widgetName, selectedNetworks, onChange, widgetConfiguration]);

    useEffect(() => {
        if (!attributes) {
            return;
        }
        const selected: { [key: string]: NetworkSelected } = {};
        attributes.forEach((attribute: any) => {
            const network = deviceList.find(n => n.deviceId === `${attribute.device_id}/${attribute.said}` && n.gatewayId === attribute.gateway_id);
            if (network) {
                selected[`${attribute.device_id}/${attribute.said}`] = {
                    ...network,
                    color: attribute.color
                };
            }

        });
        setSelectedNetworks(selected);
    }, [attributes, deviceList]);

    const onSelectNetwork = (networks: Record<string, Device>) => {
        const selected: Record<string, NetworkSelected> = { ...networks };
        setSelectedNetworks(selected);
        onChange?.({
            chartName: widgetName,
            data: Object.values(selected),
        });
    };

    const onItemRemove = (device: NetworkSelected) => {
        const selected = { ...selectedNetworks };
        delete selected[`${device.deviceId}`];
        setSelectedNetworks(selected);
        onChange?.({
            chartName: widgetName,
            data: Object.values(selected),
        });
    };

    const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        setWidgetName(name);
        onChange?.({
            chartName: name,
            data: networks,
        });
    };

    return (
        <Box className="form-box">
            <Row >
                <Col sm="12">
                    <Form.Group>
                        <Form.Label htmlFor="widget-name">Widget Name</Form.Label>
                        <Form.Control
                            id="widget-name"
                            name="widget-name"
                            type="text"
                            value={widgetName}
                            placeholder="Widget Name"
                            onChange={onNameChange}
                        />
                    </Form.Group>
                    <BreakLine
                        className={`${isEdit ? "my-4" : "my-3"}`}
                    />
                    <div className="d-flex align-items-center gap-3">
                        <Button
                            variant="primary"
                            className="mr-2"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Rooms/Zones/Groups
                        </Button>
                        {!!widgetConfiguration.maxItems && (
                            <span className="reusable-description" style={{ fontSize: 14, color: "#828FAB" }}>
                                (Max {widgetConfiguration.maxItems} rooms/groups/zones)
                            </span>
                        )}
                    </div>
                    <DashboardCreateSelectNetworkModal
                        show={showSelectModal}
                        maxSelected={widgetConfiguration.maxItems ?? 64}
                        onHide={() => setShowSelectModal(false)}
                        selected={selectedNetworks}
                        onSave={(event, data) => {
                            onSelectNetwork(data);
                        }}
                    />
                </Col>
            </Row>
            <Row className="mt-4">
                {networks.length > 0 && (
                    <SelectedList
                        data={networks}
                        onRemove={onItemRemove}
                        columns={Columns}
                    />
                )}
            </Row>

        </Box>
    );
};

export default React.memo(CreateStatusWidget);